/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* istanbul ignore file */
import * as Survey from "survey-react";
import { PersonRequest } from "@plinknz/tah-website-elements";

import { APPLIED_STATUS, BenefitType, GrantPayload } from "../../config/forms";
import { BankData } from "../../service/forms/config-generics/bank-details";
import {
  PersonalData,
  collapseSurrogate,
} from "../../service/forms/config-generics/personal-details";
import { SignatureData } from "../../service/forms/config-generics/signature";

import { getBankSVGLines } from "../svg/bank";
import { getPersonalChanges } from "../svg/member";
import { createSVG, createText } from "../svg/svg";

interface SourceData extends BankData, PersonalData, SignatureData {
  event: string;
  purpose: string;
  date: string;
  attendees: string;
  amount: string;
  budget: File[];
  arongaPutea: string;
  representRangitane: string;
  benefitRangitane: string;
  acknowledgement: string;
  promotionalMaterial: File[];
  associateLetter: File[];
}

function generateSVG({
  member,
  data,
}: {
  member: PersonRequest;
  data: SourceData;
}) {
  const personalLines = getPersonalChanges({
    ...data,
    member,
  });

  const {
    event,
    purpose,
    date,
    attendees,
    amount,
    budget,
    arongaPutea,
    representRangitane,
    benefitRangitane,
    acknowledgement,
    promotionalMaterial,
    associateLetter,
    signed,
  } = data;

  const bankLines = getBankSVGLines(data);

  const lines = [...personalLines, ...bankLines];

  lines.push(
    { heading: "Event" },
    { label: "Name", text: event },
    { label: "Purpose", text: purpose },
    { label: "Date", text: date },
    { label: "Attendees", text: attendees }
  );

  lines.push(
    { heading: "Funding" },
    { label: "Amount", text: amount },
    { label: "Budget", text: budget[0].name },
    { label: "Funding Purpose", text: arongaPutea }
  );

  lines.push(
    { heading: "Rangitāne" },
    { label: "Represent", text: representRangitane },
    { label: "Benefit", text: benefitRangitane },
    { label: "Acknowledge", text: acknowledgement }
  );

  if (promotionalMaterial?.length || associateLetter?.length) {
    const attachmentLines: typeof lines = [{ heading: "Attachments" }];
    if (promotionalMaterial?.length && promotionalMaterial[0]) {
      attachmentLines.push({
        label: "Promotional Material",
        text: promotionalMaterial[0].name,
      });
    }

    if (associateLetter?.length && associateLetter[0]) {
      attachmentLines.push({
        label: "Associate Letter",
        text: associateLetter[0].name,
      });
    }
    lines.push(...attachmentLines);
  }

  lines.push({ src: signed });

  const svg = createSVG({
    lines,
    heading: "Pūtea Tautoko Application",
  });

  const text = createText({
    lines,
    heading: "Pūtea Tautoko Application",
  });

  return { svg, text };
}

export function transformPuteaTautokoData(
  survey: Survey.ReactSurveyModel,
  member: PersonRequest
): GrantPayload {
  const surveyData = survey.data;
  const data = collapseSurrogate(surveyData);
  const now = new Date().getTime();
  const tenant = process.env.TAH_TENANT;
  const { svg, text } = generateSVG({ data, member });

  const payloadData: GrantPayload = {
    data: {
      benefit: {
        benefitDescription: BenefitType.PuteaTautoko,
        benefitStatus: APPLIED_STATUS,
        benefitDate: now,
      },
      interaction: {
        interaction: svg,
        interactionDate: now,
      },
      email: {
        name: `${data.firstName} ${data.surname}`,
        email: data.email,
        message: text,
      },
    },
    targetId: data.id || member.id,
    recaptcha: null,
    tenant,
  };

  return payloadData;
}
